import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useDeferredValue,
  useRef
} from 'react';
import { motion } from 'framer-motion';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import EmojiGrid from './components/EmojiGrid';
import SelectionTray from './components/SelectionTray';
import Toast from './components/Toast';
import Footer from './components/Footer';
import PricingSection from './components/PricingSection';
import PageHeading from './components/PageHeading';
import { allEmojis, categories } from './data';
import { translations } from './translations';
import { smartFilter } from './utils/searchUtils';

function App() {
  const [lang, setLang] = useState('zh-TW');
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const [selectedEmojis, setSelectedEmojis] = useState([]);
  const [recentEmojis, setRecentEmojis] = useState([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [skinTone, setSkinTone] = useState('default');
  const [showRecent, setShowRecent] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '' });
  const mainScrollRef = useRef(null);
  const t = translations[lang] || translations['zh-TW'];

  const handleBackToHome = useCallback(() => {
    setActiveCategory('all');
    setSearchQuery('');
    setShowRecent(false);
    setShowPricing(false);

    if (window.innerWidth < 768) {
      setIsSidebarOpen(false);
    }
  }, []);

  useEffect(() => {
    if (!mainScrollRef.current) return undefined;

    const timer = setTimeout(() => {
      mainScrollRef.current.scrollTo({
        top: 0,
        behavior: 'auto'
      });
    }, 10);

    return () => clearTimeout(timer);
  }, [
    activeCategory,
    deferredSearchQuery,
    showPricing,
    showRecent
  ]);

  useEffect(() => {
    let title = `${t.title} | ${t.subtitle}`;

    if (showPricing) {
      title = lang === 'zh-TW'
        ? '如何支持 Emoji Glint？'
        : 'How to Support Emoji Glint';
    } else if (deferredSearchQuery) {
      title = lang === 'zh-TW'
        ? `「${deferredSearchQuery}」的 Emoji 搜尋結果`
        : `Emoji Results for "${deferredSearchQuery}"`;
    } else if (activeCategory !== 'all') {
      const categoryName =
        t.categories[activeCategory] || activeCategory;
      title = `${categoryName} | ${t.title}`;
    }

    document.title = title;
  }, [
    lang,
    t,
    activeCategory,
    showPricing,
    deferredSearchQuery
  ]);

  useEffect(() => {
    const saved = localStorage.getItem('glint_recent_2026');

    if (saved) {
      setRecentEmojis(JSON.parse(saved));
    }

    const savedLang = localStorage.getItem('glint_lang');

    if (savedLang) {
      setLang(savedLang);
    }
  }, []);

  const saveToRecent = useCallback((emojiObject) => {
    setRecentEmojis((previous) => {
      const filtered = previous.filter(
        (emoji) => emoji.char !== emojiObject.char
      );
      const updated = [emojiObject, ...filtered].slice(0, 50);

      localStorage.setItem(
        'glint_recent_2026',
        JSON.stringify(updated)
      );

      return updated;
    });
  }, []);

  const filteredEmojis = useMemo(() => {
    if (showRecent) return recentEmojis;

    const query = deferredSearchQuery.trim();

    if (query) {
      return smartFilter(allEmojis, query);
    }

    if (activeCategory !== 'all') {
      return allEmojis.filter(
        (emoji) => emoji.category === activeCategory
      );
    }

    return allEmojis;
  }, [
    activeCategory,
    deferredSearchQuery,
    showRecent,
    recentEmojis
  ]);

  const handleSingleCopy = useCallback(
    (emoji, charWithTone) => {
      navigator.clipboard.writeText(charWithTone);
      saveToRecent(emoji);
      setToast({
        show: true,
        message: t.copySuccess.replace('{char}', charWithTone)
      });

      setTimeout(() => {
        setToast((previous) => ({
          ...previous,
          show: false
        }));
      }, 2000);
    },
    [saveToRecent, t]
  );

  const handleAddToTray = useCallback(
    (emoji, charWithTone) => {
      setSelectedEmojis((previous) => [
        ...previous,
        charWithTone
      ]);
      saveToRecent(emoji);
    },
    [saveToRecent]
  );

  const pageHeading = showRecent
    ? t.recentTitle
    : searchQuery
      ? t.searchResultTitle
      : t.categories[activeCategory] || activeCategory;

  return (
    <div className="flex min-h-screen bg-[#FDFDFF] text-slate-900 overflow-hidden">
      <Sidebar
        lang={lang}
        activeCategory={activeCategory}
        setActiveCategory={(id) => {
          setSearchQuery('');
          setActiveCategory(id);
          setShowRecent(false);
          setShowPricing(false);
        }}
        categories={categories}
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        onShowPricing={() => {
          setSearchQuery('');
          setShowPricing(true);
        }}
        showPricing={showPricing}
        onLogoClick={handleBackToHome}
      />

      <main className="flex-1 flex flex-col h-screen overflow-hidden relative">
        <Header
          lang={lang}
          setLang={(selectedLanguage) => {
            setLang(selectedLanguage);
            localStorage.setItem(
              'glint_lang',
              selectedLanguage
            );
          }}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          onMenuClick={() => setIsSidebarOpen(true)}
          skinTone={skinTone}
          setSkinTone={setSkinTone}
          showRecent={showRecent}
          setShowRecent={setShowRecent}
        />

        <div
          ref={mainScrollRef}
          className="flex-1 overflow-y-auto px-4 py-6 md:p-10 custom-scrollbar overscroll-contain bg-slate-50/30"
        >
          <div className="max-w-6xl mx-auto pb-64">
            {showPricing ? (
              <PricingSection lang={lang} />
            ) : (
              <>
                <header className="mb-12">
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    key={
                      activeCategory
                      + searchQuery
                      + lang
                      + showRecent
                    }
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="h-1 w-10 bg-indigo-500 rounded-full" />
                      <span className="text-xs font-black text-indigo-500 uppercase tracking-widest">
                        {searchQuery
                          ? t.statusSearch
                          : t.statusAuthority}
                      </span>
                    </div>

                    <PageHeading
                      className="text-4xl md:text-6xl font-black text-slate-800 tracking-tighter"
                      count={filteredEmojis.length}
                    >
                      {pageHeading}
                    </PageHeading>
                  </motion.div>
                </header>

                <EmojiGrid
                  emojis={filteredEmojis}
                  skinTone={skinTone}
                  onSingleCopy={handleSingleCopy}
                  onAddToTray={handleAddToTray}
                  lang={lang}
                  isSearchMode={Boolean(deferredSearchQuery)}
                  onCategoryClick={(id) =>
                    setActiveCategory(id)
                  }
                  scrollContainerRef={mainScrollRef}
                />
              </>
            )}

            <Footer lang={lang} />
          </div>
        </div>
      </main>

      <SelectionTray
        lang={lang}
        selectedEmojis={selectedEmojis}
        onRemove={(index) =>
          setSelectedEmojis((previous) =>
            previous.filter(
              (_, itemIndex) => itemIndex !== index
            )
          )
        }
        onClear={() => setSelectedEmojis([])}
        onCopyAll={() => {
          const text = selectedEmojis.join('');

          navigator.clipboard.writeText(text);
          setToast({
            show: true,
            message: t.copyComboSuccess.replace(
              '{text}',
              text
            )
          });

          setTimeout(() => {
            setToast((previous) => ({
              ...previous,
              show: false
            }));
          }, 2000);

          setSelectedEmojis([]);
        }}
      />

      <Toast
        show={toast.show}
        message={toast.message}
      />
    </div>
  );
}

export default App;